import type { CommandRibbonProps } from 'owa-command-ribbon';
import type AttachmentMultiPicker from 'owa-attachment-filepicker/lib/types/AttachmentMultiPicker';
import { getMailRibbonDefinitionsMLR } from './components/MLR/mailMultilineRibbonDefinition';
import { getMailRibbonDefinitionsSLR } from './components/SLR-and-Toolbar/mailSLRAndToolbarRibbonDefinition';
import { getIsViewModeSelected, getRibbonViewMode } from 'owa-command-ribbon-store';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { getExtraBottomRibbonControls } from './util/getExtraBottomRibbonControls';
import { getExtraTopRibbonControls } from './util/getExtraTopRibbonControls';
import { getMailIconColorMap } from './util/mailIconColorMap';
import { listViewStore } from 'owa-mail-list-store';
import { lazySaveSelectedTab, getStore as getRibbonStore } from 'owa-mail-ribbon-store';
import CommandingModeMenuStrings from 'owa-locstrings/lib/strings/commandingModeMenu.locstring.json';
import { getStringRibbonId, MailRibbonTabId } from 'owa-ribbon-ids';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getFullComposeShowingInTabContentArea from 'owa-mail-compose-actions/lib/selectors/getFullComposeShowingInTabContentArea';
import {
    getFirstRenderCompletedOnComposeTab,
    getActiveContentTab,
    lazySetFirstRenderCompleted,
} from 'owa-tab-store';
import { logUsage } from 'owa-analytics';
import { DatapointVariant } from 'owa-analytics-types';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getInlineComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';

export function getMailCommandRibbonProps(
    targetWindow: Window,
    attachmentInlinePickers: AttachmentMultiPicker,
    attachmentMultiPicker: AttachmentMultiPicker
): CommandRibbonProps {
    const ribbonProps: RibbonControlDefinitionProps = {
        popoutComposeViewState: undefined,
        attachmentPickers: {
            addFilesAsAttachments: attachmentMultiPicker,
            addImagesInline: attachmentInlinePickers,
        },
        targetWindow,
        showMoveRules: true,
    };
    return {
        getSingleLineRibbonDefinition: () => getMailRibbonDefinitionsSLR(ribbonProps),
        getMultiLineRibbonDefinition: () => getMailRibbonDefinitionsMLR(ribbonProps),
        getDefaultSelectedTabId: getDefaultSelectedTabId,
        onTabClick: onTabClick,
        getHideTopRibbon: () => getIsViewModeSelected(CommandingViewMode.CommandBar),
        getExtraTopRibbonControls: getExtraTopRibbonControls,
        getExtraBottomRibbonControls: getExtraBottomRibbonControls,
        iconColorMap: getMailIconColorMap(),
        onRibbonUpdate: onRibbonUpdate,
        thisWindow: targetWindow,
    };
}

function onTabClick(tabId: string) {
    //saves the selected tab when a new tab is clicked
    lazySaveSelectedTab.importAndExecute(tabId);
}

const viewModeToStringMap: { [viewMode in CommandingViewMode]: string } = {
    [CommandingViewMode.CommandBar]: CommandingModeMenuStrings.toolbarLabel,
    [CommandingViewMode.SingleLineRibbon]: CommandingModeMenuStrings.singleLineRibbonLabel,
    [CommandingViewMode.MultiLineRibbon]: CommandingModeMenuStrings.multiLineRibbonLabel,
};

const readOnlyRibbonTabsArray = [MailRibbonTabId.Tab_Home, MailRibbonTabId.Tab_View];
function getDefaultSelectedTabId() {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId);
    const isNotesFolder = tableView?.tableQuery?.folderId
        ? folderIdToName(tableView?.tableQuery.folderId) === 'notes'
        : false;

    // If we're in the notes folder, always move user to the Notes tab. We remove this tab when the
    // user leaves the notes folder.
    if (isNotesFolder) {
        return MailRibbonTabId.Tab_Notes;
    }

    const isFullCompose = getFullComposeShowingInTabContentArea() !== null;
    const isInlineComposeViewState: boolean = !!getInlineComposeViewState();
    let selectedRibbonTabFromStore =
        isFullCompose || isInlineComposeViewState
            ? isFeatureEnabled('cmp-m3-ribbonTabs')
                ? MailRibbonTabId.Tab_M3Message
                : MailRibbonTabId.Tab_Message
            : MailRibbonTabId.Tab_Home;

    const activeReadingPaneTab = getActiveContentTab();
    const activeReadingPaneTabId = activeReadingPaneTab.id;
    if (
        !isInlineComposeViewState &&
        (!isFullCompose || getFirstRenderCompletedOnComposeTab(activeReadingPaneTabId))
    ) {
        const currentStoreSelectedTab = getRibbonStore().selectedTab;

        // if it's read-only reading-pane tab and the selected ribbon tab doesn't exist on the read-only reading pane tab, then move it to default ("Home")
        if (isFullCompose || readOnlyRibbonTabsArray.includes(currentStoreSelectedTab)) {
            selectedRibbonTabFromStore = currentStoreSelectedTab;
        }
    }
    return selectedRibbonTabFromStore;
}

function onRibbonUpdate() {
    // Log initial ribbon preference and only log it once
    logUsage(
        'RibbonPreference',
        { ribbonType: viewModeToStringMap[getRibbonViewMode()] },
        { variant: DatapointVariant.WarmOnly }
    );

    // After rendering CommandRibbon, update store to sync selectedTab
    // getDefaultSelectedTabId function uses FirstRenderCompleted from the store,
    // therefore the store is updated with selected tab Before we mark the tab with FirstRenderCompleted
    lazySaveSelectedTab.importAndExecute(getStringRibbonId(getDefaultSelectedTabId()));

    // when opening a new compose tab, the ribbon should be "Message" by default.
    // when selecting a pre-existing compose tab, the ribbon tab shouldn't change.
    // after the first render of a new compose tab, the TabViewState is updated.
    const activeReadingPaneTabId = getActiveContentTab().id;
    const firstRibbonRenderCompletedOnComposeTab = getFirstRenderCompletedOnComposeTab(
        activeReadingPaneTabId
    );

    // After rendering CommandRibbon for compose tab, update MailComposeTabViewState the first render was completed,
    // so that next time the ribbon tab will not be moved to "Message" by default for this compose tab
    const isFullCompose = getFullComposeShowingInTabContentArea() !== null;
    if (isFullCompose && !firstRibbonRenderCompletedOnComposeTab) {
        lazySetFirstRenderCompleted.importAndExecute(activeReadingPaneTabId);
    }
}
